import { Layer, Source, useMap } from "@vis.gl/react-maplibre";
import { LngLatBounds } from "maplibre-gl";
import { FC, useEffect, useMemo } from "react";
import useEvent from "react-use-event-hook";

import { fitMapLibreBoundsSafely } from "./maplibre-bounds.utils";
import { MapLibreLassoShape } from "./maplibre-lasso-shape";
import { getAliveMap } from "./maplibre-map.utils";
import {
  POLYGON_EDITOR_EMPTY_HANDLES,
  POLYGON_EDITOR_FILL_LAYER_ID,
  POLYGON_EDITOR_LINE_LAYER_ID,
  POLYGON_EDITOR_MIDPOINT_LAYER_ID,
  POLYGON_EDITOR_MIDPOINT_OPACITY,
  POLYGON_EDITOR_MIDPOINT_RADIUS_PX,
  POLYGON_EDITOR_MIDPOINTS_SOURCE_ID,
  POLYGON_EDITOR_SHAPES_SOURCE_ID,
  POLYGON_EDITOR_VERTEX_FILL_COLOR,
  POLYGON_EDITOR_VERTEX_LAYER_ID,
  POLYGON_EDITOR_VERTEX_RADIUS_PX,
  POLYGON_EDITOR_VERTEX_STROKE_COLOR,
  POLYGON_EDITOR_VERTEX_STROKE_WIDTH_PX,
  POLYGON_EDITOR_VERTICES_SOURCE_ID,
} from "./maplibre-polygon-editor.constants";
import { IMapLibrePolygonEditorProps } from "./maplibre-polygon-editor.models";
import {
  buildEditorMidpointsCollection,
  buildEditorShapesCollection,
  buildEditorVerticesCollection,
  cutEditablePolygons,
  toEditorRing,
} from "./maplibre-polygon-editor.utils";
import { useMapLibreLassoSelect } from "./use-maplibre-lasso-select";
import { useMapLibrePolygonEditorPointer } from "./use-maplibre-polygon-editor-pointer";
import { useMapLibrePolygonEditorState } from "./use-maplibre-polygon-editor-state";

/**
 * Shape editing on MapLibre — the twin of the two Leaflet-geoman editors: the areas map's in-place reshape
 * and the polygon drawer's full toolbar.
 *
 * Shapes are one source with a fill and a line layer. Corners and midpoints are GPU circles rather than an
 * HTML element each, which is what lets a boundary with thousands of corners stay editable; every gesture
 * hit-tests them instead of relying on their own listeners.
 *
 * Drawing a new shape and tracing a cut are the same gesture the stations lasso uses, because it is the
 * same gesture geoman used — down to the magnet on the first corner.
 */
export const MapLibrePolygonEditor: FC<IMapLibrePolygonEditorProps> = ({
  color,
  fillOpacity,
  weight,
  mode = "none",
  value,
  handleRef,
  fitPadding,
  onGestureComplete,
  traceControlsRef,
}) => {
  const { current: mapRef } = useMap();
  const { polygons, polygonsRef, commit, createId, fitRequestId } =
    useMapLibrePolygonEditorState({ value, handleRef });

  useMapLibrePolygonEditorPointer({ mode, polygonsRef, commit });

  const shapes = useMemo(
    () => buildEditorShapesCollection(polygons),
    [polygons],
  );
  const isReshaping = mode === "edit";
  const vertices = useMemo(
    () =>
      isReshaping
        ? buildEditorVerticesCollection(polygons)
        : POLYGON_EDITOR_EMPTY_HANDLES,
    [polygons, isReshaping],
  );
  const midpoints = useMemo(
    () =>
      isReshaping
        ? buildEditorMidpointsCollection(polygons)
        : POLYGON_EDITOR_EMPTY_HANDLES,
    [polygons, isReshaping],
  );

  /*
   * Fit only when shapes arrive from outside — the initial load and each import. Reshaping must never
   * move the map out from under the pointer.
   *
   * The seed usually lands before the style is up, and MapLibre silently drops a `fitBounds` issued then,
   * so the fit waits for `load` rather than firing into the void.
   */
  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !fitRequestId || fitPadding === undefined) {
      return;
    }

    const fitToShapes = (): void => {
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      const bounds = new LngLatBounds();
      let hasAnyPoint = false;

      polygonsRef.current.forEach(({ rings }) =>
        rings.forEach(ring =>
          ring.forEach(position => {
            bounds.extend(position);
            hasAnyPoint = true;
          }),
        ),
      );

      if (hasAnyPoint) {
        // Same clamp/restore as ride maps: a short editor canvas + large padding used to NaN the zoom.
        fitMapLibreBoundsSafely({
          map: aliveMap,
          bounds,
          padding: fitPadding,
        });
      }
    };

    if (map.isStyleLoaded()) {
      fitToShapes();

      return;
    }

    map.once("load", fitToShapes);

    return () => {
      getAliveMap(mapRef)?.off("load", fitToShapes);
    };
  }, [mapRef, fitRequestId, polygonsRef, fitPadding]);

  // Drawing a new shape and tracing a cut are the same gesture, so they share one tool; only what happens
  // to the finished ring differs.
  const handleTraceComplete = useEvent((traced: [number, number][]): void => {
    const ring = toEditorRing(traced);

    commit(
      mode === "cut"
        ? cutEditablePolygons({
            polygons: polygonsRef.current,
            cutRing: ring,
            createId,
          })
        : [...polygonsRef.current, { id: createId(), rings: [ring] }],
    );
    onGestureComplete?.();
  });

  const handleTraceCancel = useEvent((): void => onGestureComplete?.());

  const isTracing = mode === "draw" || mode === "cut";
  const { points, cursor, finish, removeLastVertex, cancel } =
    useMapLibreLassoSelect({
      isActive: isTracing,
      onComplete: handleTraceComplete,
      onCancel: handleTraceCancel,
    });

  // Published rather than passed down, because the actions row lives in the toolbar outside the map.
  useEffect(() => {
    if (!traceControlsRef) {
      return;
    }

    traceControlsRef.current = { finish, removeLastVertex, cancel };

    return () => {
      traceControlsRef.current = undefined;
    };
  }, [traceControlsRef, finish, removeLastVertex, cancel]);

  return (
    <>
      <Source id={POLYGON_EDITOR_SHAPES_SOURCE_ID} type="geojson" data={shapes}>
        <Layer
          id={POLYGON_EDITOR_FILL_LAYER_ID}
          type="fill"
          paint={{
            ["fill-color"]: color,
            ["fill-opacity"]: fillOpacity,
          }}
        />
        <Layer
          id={POLYGON_EDITOR_LINE_LAYER_ID}
          type="line"
          paint={{
            ["line-color"]: color,
            ["line-width"]: weight,
          }}
        />
      </Source>

      <Source
        id={POLYGON_EDITOR_MIDPOINTS_SOURCE_ID}
        type="geojson"
        data={midpoints}
      >
        <Layer
          id={POLYGON_EDITOR_MIDPOINT_LAYER_ID}
          type="circle"
          paint={{
            ["circle-radius"]: POLYGON_EDITOR_MIDPOINT_RADIUS_PX,
            ["circle-color"]: POLYGON_EDITOR_VERTEX_FILL_COLOR,
            ["circle-opacity"]: POLYGON_EDITOR_MIDPOINT_OPACITY,
            ["circle-stroke-width"]: POLYGON_EDITOR_VERTEX_STROKE_WIDTH_PX,
            ["circle-stroke-color"]: POLYGON_EDITOR_VERTEX_STROKE_COLOR,
            ["circle-stroke-opacity"]: POLYGON_EDITOR_MIDPOINT_OPACITY,
          }}
        />
      </Source>

      {/* After the midpoints, so a corner always wins the hit test where the two overlap. */}
      <Source
        id={POLYGON_EDITOR_VERTICES_SOURCE_ID}
        type="geojson"
        data={vertices}
      >
        <Layer
          id={POLYGON_EDITOR_VERTEX_LAYER_ID}
          type="circle"
          paint={{
            ["circle-radius"]: POLYGON_EDITOR_VERTEX_RADIUS_PX,
            ["circle-color"]: POLYGON_EDITOR_VERTEX_FILL_COLOR,
            ["circle-stroke-width"]: POLYGON_EDITOR_VERTEX_STROKE_WIDTH_PX,
            ["circle-stroke-color"]: POLYGON_EDITOR_VERTEX_STROKE_COLOR,
          }}
        />
      </Source>

      {isTracing && <MapLibreLassoShape points={points} cursor={cursor} />}
    </>
  );
};
